const { getFocusableElements } = window.BAO.Utils

class SiteModal extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['backdrop']
  }

  static get requiredExternalElements () {
    return ['toggle']
  }

  connectedCallback () {
    super.connectedCallback()

    this.close = this.close.bind(this)
    this.open = this.open.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleTabPress = this.handleTabPress.bind(this)

    /** @type {HTMLElement} */
    this.elementUsedToOpenModal = null
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document.body, 'site-modal:open', event =>
      this.handleSiteModalOpen(event),
    )

    this.listeners.add(this.els.backdrop.element, 'click', () => this.close())

    if (this.els.close.exists) {
      this.els.close.elements.forEach(button => {
        this.listeners.add(button, 'click', () => this.close())
      })
    }

    this.externalToggles.forEach(toggle => {
      this.listeners.add(toggle, 'click', () => this.open())
    })
  }

  open () {
    this.isOpen = true
  }

  close () {
    this.isOpen = false
  }

  /** @param {CustomEvent<{ id: string; }>} event */
  handleSiteModalOpen (event) {
    if (event.detail.id === this.id) {
      this.open()
    }
  }

  /** @param {KeyboardEvent} event */
  handleKeydown (event) {
    /** @type {Record<string, (event: KeyboardEvent) => void} */
    const shortcuts = {
      Tab: this.handleTabPress,
      Escape: this.close,
    }

    const action = shortcuts[event.key]

    if (!action) {
      return
    }

    action(event)
  }

  /** @param {KeyboardEvent} event */
  handleTabPress (event) {
    const {
      0: first,
      [this.focusableElements.length - 1]: last,
    } = this.focusableElements

    const shouldLoopToFirstElement = !event.shiftKey && event.target === last
    const shouldLoopToLastElement = event.shiftKey && event.target === first
    const shouldOverrideDefault =
      shouldLoopToFirstElement || shouldLoopToLastElement

    if (shouldOverrideDefault) {
      event.preventDefault()
    }

    if (shouldLoopToFirstElement) {
      first.focus()
    } else if (shouldLoopToLastElement) {
      last.focus()
    }
  }

  get isOpen () {
    return this.getAttribute('aria-hidden') === 'false'
  }

  set isOpen (isOpen) {
    this.setAttribute('aria-hidden', !isOpen)

    document.body.classList.toggle('util-SiteOverflowed', isOpen)

    if (isOpen) {
      this.elementUsedToOpenModal = document.activeElement

      this.dispatchEvent(
        new CustomEvent('site-modal:opened', { bubbles: true }),
      )

      window.setTimeout(() => {
        this.els.close?.element.focus()

        this.listeners.add(document.body, 'keydown', this.handleKeydown)
      }, 200)
    } else {
      this.listeners.remove('keydown', this.handleKeydown)

      this.elementUsedToOpenModal?.focus()
      this.elementUsedToOpenModal = null

      this.dispatchEvent(
        new CustomEvent('site-modal:closed', { bubbles: true }),
      )
    }
  }

  /** @returns {HTMLButtonElement[]} */
  get externalToggles () {
    return this.externalEls.toggle.elements.filter(
      toggle => toggle.dataset.siteModalId === this.id,
    )
  }

  get focusableElements () {
    return getFocusableElements(this)
  }
}

if (!customElements.get('site-modal')) {
  customElements.define('site-modal', SiteModal)
}
